import type {CopilotSpace} from '@github-ui/copilot-chat-types'
import {SpaceDialogFooter} from '../SpaceDialogFooter'
import {Banner, FormControl, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useState} from 'react'

import styles from './InstructionsDialog.module.css'

interface InstructionsDialogProps {
  copilotSpace: CopilotSpace
  onClose: () => void
  onSaved?: (updatedSpace: CopilotSpace) => void
  onSave: (instructions: string) => Promise<CopilotSpace>
  isSaving: boolean
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}

const MAX_GENERAL_INSTRUCTIONS_LENGTH = 4000

export function InstructionsDialog({
  copilotSpace,
  onClose,
  onSaved,
  onSave,
  isSaving,
  returnFocusRef,
}: InstructionsDialogProps) {
  const [instructions, setInstructions] = useState(copilotSpace.generalInstructions ?? '')
  const [error, setError] = useState<string | null>(null)

  async function saveInstructions() {
    try {
      /* upsertCopilotSpace returns the updated space */
      const updatedSpace = await onSave(instructions)
      onSaved?.(updatedSpace)
      onClose()
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Failed to save instructions')
      }
    }
  }

  function handleCancel() {
    setInstructions(copilotSpace.generalInstructions ?? '')
    onClose()
  }

  return (
    <Dialog
      width="xlarge"
      height="large"
      title="Instructions"
      onClose={handleCancel}
      returnFocusRef={returnFocusRef}
      position={{narrow: 'fullscreen'}}
      renderBody={() => (
        <div className={styles.instructionsContainer}>
          {error && <Banner variant="critical" title={error} className="mb-2" />}
          <FormControl className={styles.grow} disabled={!copilotSpace.editable}>
            <FormControl.Label visuallyHidden>Instructions</FormControl.Label>
            <Textarea
              block
              value={instructions}
              onChange={e => setInstructions(e.target.value)}
              placeholder="Define Copilot's role, focus, and what to avoid in this space. Ex. “You are a [role]. Follow our
                [framework/patterns] to [type of task]. Avoid [practices or tools] unless specified”."
              resize="none"
              className={styles.grow}
            />
            <CharacterCount text={instructions} promptCharLimit={MAX_GENERAL_INSTRUCTIONS_LENGTH} />
          </FormControl>
        </div>
      )}
      renderFooter={() =>
        copilotSpace.editable && (
          <SpaceDialogFooter
            onCancel={handleCancel}
            onSave={saveInstructions}
            isLoading={isSaving}
            loadingAnnouncement="Saving instructions"
            saveButtonText="Save"
          />
        )
      }
    />
  )
}

function CharacterCount({text, promptCharLimit}: {text: string; promptCharLimit: number}) {
  const tooLong = text.length > promptCharLimit
  const currentCount = (
    <>
      {text.length.toLocaleString()} / {promptCharLimit.toLocaleString()} characters
    </>
  )
  return tooLong ? (
    <FormControl.Validation variant="error">{currentCount}</FormControl.Validation>
  ) : (
    // eslint-disable-next-line primer-react/direct-slot-children
    <FormControl.Caption>{currentCount}</FormControl.Caption>
  )
}
